/**
 * Block: Employees
 *
 * Clicking an employee opens the matching modal. The `show` class flips the
 * modal to `display: grid`; `anim` is added on the next frame so the opacity
 * transition has a state to animate from and the modal fades in smoothly.
 *
 * While a modal is open (below the breakpoint) the body gets `no-scroll`, and a
 * click outside the modal — or on its close icon — dismisses it.
 *
 * Each employees block on the page wires up independently with its own open
 * state, so multiple blocks can coexist.
 */

// From this width up the modal aligns to the clicked employee instead of being
// centred in the viewport (see the CSS default).
const alignQuery = window.matchMedia('(min-width: 768px)');

function initEmployeesBlock(section) {
  const links = section.querySelectorAll('.employee-link');
  const modals = section.querySelectorAll('.employee-modal');
  let openModalEl = null;
  // The elements currently faded out while a modal is open, so they can be
  // restored on close.
  let fadedEls = [];

  // The <li> employees in the same grid row as `li`, in DOM (left-to-right)
  // order. Employees in a row share the same offsetTop.
  function rowItems(li) {
    const items = section.querySelectorAll('ul > li');
    return Array.prototype.filter.call(items, function (item) {
      return item.offsetTop === li.offsetTop;
    });
  }

  function openModal(modal, link) {
    openModalEl = modal;

    const li = link.closest('li');
    const row = rowItems(li);

    // Fade out the first employee of the clicked employee's row.
    fadedEls = row[0].querySelectorAll('.name, .position');
    fadedEls.forEach(function (el) {
      el.classList.add('fade-out');
    });

    if (alignQuery.matches) {
      // Pin the modal's top to the clicked employee's top. The modal is
      // position:absolute inside the <ul>, so offset relative to the <ul>.
      const ul = li.closest('ul');
      const top = li.getBoundingClientRect().top - ul.getBoundingClientRect().top;
      modal.style.top = top + 'px';

      // Give the info block at least the height of the tallest employee in the
      // row, so the modal covers the row it's overlaid on.
      const tallest = row.reduce(function (max, item) {
        return Math.max(max, item.offsetHeight);
      }, 0);
      const info = modal.querySelector('.employee-info');
      if (info) {
        info.style.minHeight = tallest + 'px';
      }
    } else {
      // Below the breakpoint the modal is centred and covers the screen, so
      // lock body scrolling behind it.
      document.body.classList.add('no-scroll');
    }

    modal.classList.add('show');
    // Next frame: the modal is now displayed, so toggling `anim` transitions.
    requestAnimationFrame(() => {
      modal.classList.add('anim');
    });
  }

  function closeModal(modal) {
    openModalEl = null;
    fadedEls.forEach(function (el) {
      el.classList.remove('fade-out');
    });
    fadedEls = [];
    modal.classList.remove('anim');
    document.body.classList.remove('no-scroll');
    // Wait for the fade-out before removing it from the layout.
    modal.addEventListener(
      'transitionend',
      () => {
        modal.classList.remove('show');
        // Clear the inline positioning so the centred CSS default returns.
        modal.style.top = '';
        const info = modal.querySelector('.employee-info');
        if (info) {
          info.style.minHeight = '';
        }
      },
      { once: true }
    );
  }

  links.forEach(function (link) {
    link.addEventListener('click', function (e) {
      e.preventDefault();
      // Keep this click from reaching the outside-click handler below.
      e.stopPropagation();

      // If a modal is already open, close it instead of opening another.
      if (openModalEl) {
        closeModal(openModalEl);
        return;
      }

      const id = link.closest('li').id;
      const modal = section.querySelector(
        '.employee-modal[data-id="' + id + '"]'
      );

      if (modal) {
        openModal(modal, link);
      }
    });
  });

  // Close on the modal's close icon.
  modals.forEach(function (modal) {
    const closeIcon = modal.querySelector('svg');
    if (closeIcon) {
      closeIcon.addEventListener('click', function () {
        closeModal(modal);
      });
    }
  });

  // Close when clicking anywhere outside this block's open modal.
  document.addEventListener('click', function (e) {
    if (openModalEl && !openModalEl.contains(e.target)) {
      closeModal(openModalEl);
    }
  });
}

document.querySelectorAll('section.employees').forEach(initEmployeesBlock);
